/**
 * Shared "schedule" UI primitives used by both the Number detail page
 * (where each schedule row references an Application) and the
 * Application detail page (where each row references a DNIS). Lifted
 * out of NumberEditPage.tsx so both perspectives render the exact same
 * Active / Scheduled / Expired chip.
 *
 * The chip carries the colour for the row: green for Active, amber for
 * Scheduled, muted grey for Expired (or any unknown state). The body
 * cells (name / phone-number / date) stay neutral so the chip is the
 * single piece of chroma in the row.
 */

const PALETTE: Record<string, { bg: string; fg: string; bd: string }> = {
  active: {
    bg: "var(--portal-status-success-soft)",
    fg: "var(--portal-status-success-darker)",
    bd: "var(--portal-status-success)",
  },
  scheduled: { bg: "#fff7e6", fg: "#a36a00", bd: "#f1b230" },
  expired: {
    bg: "var(--portal-surface)",
    fg: "var(--portal-muted)",
    bd: "var(--portal-border)",
  },
};

/**
 * Pill rendering one of the three scheduled-application states. The
 * lookup is forgiving: any unknown state name falls back to the muted
 * expired palette so unexpected SP values never cause a UI crash.
 */
export function ScheduleStateChip({ state }: { state: string }) {
  const c = PALETTE[state?.toLowerCase()] ?? PALETTE.expired;
  return (
    <span
      style={{
        display: "inline-block",
        padding: "3px 10px",
        borderRadius: 999,
        border: "1px solid",
        background: c.bg,
        color: c.fg,
        borderColor: c.bd,
        fontSize: "0.8rem",
        fontWeight: 600,
      }}
    >
      {state || "—"}
    </span>
  );
}

/**
 * Same DD-MM-YYYY HH:MM rendering both detail pages use for ValidFrom
 * timestamps. Treats invalid / empty inputs as "—" so the call site
 * doesn't need to guard. Times are shown in the user's local timezone
 * (which matches the legacy portal's "schedule in your own clock"
 * convention).
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return `${d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  })} ${d.toLocaleTimeString("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
}
